import React, { useCallback, useEffect, useState } from 'react';
import { Activity, AlertTriangle, KeyRound, LogIn, LockKeyhole, RotateCw, ShieldCheck } from 'lucide-react';
import ENTITY_ICONS from './common/entityIcons';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from './ui/Card';
import { Button } from './ui/Button';
import { Link } from 'react-router-dom';
import { useApps } from '../contexts/AppsContext';
import dashboardService from '../services/dashboard';

const FOCUS_RING = 'outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-offset-2';

/**
 * One column of a vertical bar chart. Bars aren't links (no list page filters by date); instead
 * each bar is focusable and shows its value above the bar on hover/focus.
 * Layout: column is h-full, bar area flex-1 min-h-0 so % heights resolve; non-zero bars get a 2px floor.
 */
const BarColumn = ({ value, max, label, tooltip, barClass, gapClass = 'gap-2', children }) => {
    const n = parseInt(value, 10) || 0;
    const height = (n / max) * 100;
    return (
        <div className={`flex-1 min-w-0 h-full flex flex-col items-center ${gapClass}`}>
            <div
                tabIndex={0}
                role="img"
                aria-label={`${label}: ${tooltip}`}
                className={`group relative w-full flex-1 min-h-0 flex items-end rounded-t ${FOCUS_RING}`}
            >
                <div
                    className={`relative w-full rounded-t transition-colors ${barClass}`}
                    style={{ height: `${height}%`, minHeight: height > 0 ? 2 : 0 }}
                >
                    <span
                        aria-hidden="true"
                        className="pointer-events-none absolute bottom-full left-1/2 -translate-x-1/2 mb-1 z-10 whitespace-nowrap rounded bg-slate-900 px-1.5 py-0.5 text-[11px] font-medium text-white opacity-0 shadow transition-opacity group-hover:opacity-100 group-focus-visible:opacity-100"
                    >
                        {label}: {tooltip}
                    </span>
                </div>
            </div>
            {children}
        </div>
    );
};

const pad = (n) => String(n).padStart(2, '0');
const monthKey = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}`;
const dayKey = (d) => `${monthKey(d)}-${pad(d.getDate())}`;
const toInt = (v) => parseInt(v, 10) || 0;

/**
 * Fill a sparse series (the API returns only periods with rows) so the x-axis covers the whole
 * window the title promises. Keys present in the data but outside the generated window (e.g.
 * server/browser timezone skew) are kept, so no returned value is ever dropped.
 */
const fillSeries = (rows, keyField, valueField, keys) => {
    const values = new Map((rows || []).map((r) => [String(r[keyField]), toInt(r[valueField])]));
    const all = Array.from(new Set([...keys, ...values.keys()])).sort();
    return all.map((key) => ({ key, value: values.get(key) || 0 }));
};

const lastMonths = (n) => {
    const now = new Date();
    return Array.from({ length: n }, (_, i) => monthKey(new Date(now.getFullYear(), now.getMonth() - (n - 1 - i), 1)));
};

const lastDays = (n) => {
    const now = new Date();
    return Array.from({ length: n + 1 }, (_, i) =>
        dayKey(new Date(now.getFullYear(), now.getMonth(), now.getDate() - (n - i)))
    );
};

const formatMonth = (key) => {
    const [y, m] = key.split('-').map(Number);
    return new Date(y, m - 1).toLocaleDateString('en-US', { month: 'short', year: '2-digit' });
};

const formatDay = (key) => {
    // 'YYYY-MM-DD': build a local date so the label doesn't shift a day in negative UTC offsets.
    const [y, m, d] = key.split('-').map(Number);
    return new Date(y, (m || 1) - 1, d || 1).toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
};

const peakOf = (series) => series.reduce((best, p) => (p.value > (best?.value ?? 0) ? p : best), null);

/** Chart card: title says exactly what is counted and over what window; summary line is plain text. */
const ChartCard = ({ icon: Icon, title, description, series, unit, formatKey, barClass, dense, summary }) => {
    const max = Math.max(1, ...series.map((p) => p.value));
    const total = series.reduce((sum, p) => sum + p.value, 0);
    return (
        <Card className="overflow-x-clip">
            <CardHeader>
                <CardTitle className="flex items-center gap-2">
                    <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                    {title}
                </CardTitle>
                <CardDescription>{description}</CardDescription>
            </CardHeader>
            <CardContent>
                {total === 0 ? (
                    <p className="py-8 text-center text-sm text-slate-600">None in this period.</p>
                ) : (
                    <div className="space-y-3 pt-6">
                        <div className={`flex h-40 items-end ${dense ? 'gap-px sm:gap-0.5' : 'gap-1 sm:gap-2'}`}>
                            {series.map((p) => (
                                <BarColumn
                                    key={p.key}
                                    value={p.value}
                                    max={max}
                                    label={formatKey(p.key)}
                                    tooltip={unit(p.value)}
                                    barClass={barClass}
                                    gapClass={dense ? 'gap-0' : 'gap-2'}
                                >
                                    {!dense && (
                                        <span className="hidden max-w-full truncate whitespace-nowrap text-[11px] text-slate-600 sm:block">
                                            {formatKey(p.key)}
                                        </span>
                                    )}
                                </BarColumn>
                            ))}
                        </div>
                        {series.length > 0 && (
                            <div
                                className={`flex justify-between text-xs text-slate-600 ${dense ? '' : 'sm:hidden'}`}
                                aria-hidden="true"
                            >
                                <span>{formatKey(series[0].key)}</span>
                                <span>{formatKey(series[series.length - 1].key)}</span>
                            </div>
                        )}
                        <p className="border-t border-slate-200 pt-3 text-sm text-slate-600">
                            {summary(total, peakOf(series))}
                        </p>
                    </div>
                )}
            </CardContent>
        </Card>
    );
};

const plural = (n, one, many = `${one}s`) => `${n.toLocaleString()} ${n === 1 ? one : many}`;

const ActivityCharts = () => {
    const [state, setState] = useState({ status: 'loading', data: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, status: 'loading' }));
        try {
            const data = await dashboardService.getAnalytics();
            if (!data) throw new Error('Empty analytics response');
            setState({ status: 'ready', data });
        } catch (err) {
            console.error('Error loading analytics:', err);
            setState({ status: 'error', data: null });
        }
    }, []);

    useEffect(() => {
        load();
    }, [load]);

    if (state.status === 'loading') {
        return (
            <div className="grid gap-6 lg:grid-cols-2" aria-busy="true" aria-label="Loading charts">
                {[1, 2, 3].map((i) => (
                    <div
                        key={i}
                        className={`h-72 animate-pulse rounded-xl bg-slate-200 ${i === 3 ? 'lg:col-span-2' : ''}`}
                    />
                ))}
            </div>
        );
    }

    if (state.status === 'error') {
        return (
            <Card role="alert">
                <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                        <p className="text-sm text-slate-700">
                            Couldn’t load the charts. Nothing is shown rather than empty bars. Try again.
                        </p>
                    </div>
                    <Button variant="outline" onClick={load}>
                        <RotateCw className="h-4 w-4" />
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    }

    const { data } = state;
    const months = lastMonths(12);
    const accounts = fillSeries(data.userGrowth, 'month', 'user_count', months);
    const memberships = fillSeries(data.appUsage, 'month', 'user_count', months);
    const sessions = fillSeries(data.activeSessions, 'date', 'session_count', lastDays(30));

    return (
        <div className="grid gap-6 lg:grid-cols-2">
            <ChartCard
                icon={ENTITY_ICONS.users}
                title="New user accounts by month"
                description="Accounts created in each of the last 12 months."
                series={accounts}
                unit={(n) => plural(n, 'account')}
                formatKey={formatMonth}
                barClass="bg-primary-600 group-hover:bg-primary-700 group-focus-visible:bg-primary-700"
                summary={(total, peak) =>
                    `${plural(total, 'account')} created · busiest month ${formatMonth(peak.key)} (${peak.value})`
                }
            />
            <ChartCard
                icon={ENTITY_ICONS.apps}
                title="Users given app access by month"
                description="Distinct users whose current app membership was created that month, last 12 months."
                series={memberships}
                unit={(n) => plural(n, 'user')}
                formatKey={formatMonth}
                barClass="bg-emerald-600 group-hover:bg-emerald-700 group-focus-visible:bg-emerald-700"
                summary={(total, peak) =>
                    `Monthly counts sum to ${total.toLocaleString()} · busiest month ${formatMonth(peak.key)} (${
                        peak.value
                    })`
                }
            />
            <div className="lg:col-span-2">
                <ChartCard
                    icon={Activity}
                    title="Sign-in sessions started per day"
                    description="Sessions created on each of the last 30 days, all applications."
                    series={sessions}
                    unit={(n) => plural(n, 'session')}
                    formatKey={formatDay}
                    barClass="bg-blue-600 group-hover:bg-blue-700 group-focus-visible:bg-blue-700"
                    dense
                    summary={(total, peak) =>
                        `${plural(total, 'session')} in 30 days · average ${Math.round(
                            total / sessions.length
                        )} a day · busiest ${formatDay(peak.key)} (${peak.value})`
                    }
                />
            </div>
        </div>
    );
};

// --- Account health --------------------------------------------------------------------------

const formatDate = (iso) =>
    new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const share = (n, total) => {
    if (!total || !n) return '0%';
    const pct = (n / total) * 100;
    return pct < 1 ? '<1%' : `${Math.round(pct)}%`;
};

// Ordered recent → stale → unknown. Amber marks the stale bucket; slate is "no record".
const SIGN_IN_BUCKETS = {
    within_7d: { label: 'Last 7 days', swatch: 'bg-primary-700' },
    within_30d: { label: '8–30 days ago', swatch: 'bg-primary-500' },
    within_90d: { label: '31–90 days ago', swatch: 'bg-primary-300' },
    over_90d: { label: 'More than 90 days ago', swatch: 'bg-amber-500' },
    none_recorded: { label: 'No sign-in on record', swatch: 'bg-slate-300' },
};

const PASSWORD_BUCKETS = {
    within_30d: { label: 'Changed in the last 30 days', swatch: 'bg-primary-700' },
    within_90d: { label: '31–90 days ago', swatch: 'bg-primary-500' },
    within_180d: { label: '91–180 days ago', swatch: 'bg-primary-300' },
    over_180d: { label: 'More than 180 days ago', swatch: 'bg-amber-500' },
    none_recorded: { label: 'No change recorded', swatch: 'bg-slate-300' },
};

/**
 * One horizontal bar split into buckets that partition `total`, plus a row per bucket with the
 * count and share. The bar is decorative (aria-hidden); the rows carry the numbers. Buckets are
 * not links: no list page can filter users by sign-in or password date.
 */
const BucketBreakdown = ({ buckets, meta, total }) => (
    <div className="space-y-4">
        <div className="flex h-3 w-full gap-px overflow-hidden rounded-full bg-slate-100" aria-hidden="true">
            {buckets
                .filter((b) => b.count > 0)
                .map((b) => (
                    <div key={b.key} className={meta[b.key].swatch} style={{ width: `${(b.count / total) * 100}%` }} />
                ))}
        </div>
        <ul className="divide-y divide-slate-100 text-sm">
            {buckets.map((b) => (
                <li key={b.key} className="flex items-center gap-3 py-2">
                    <span className={`h-2.5 w-2.5 shrink-0 rounded-sm ${meta[b.key].swatch}`} aria-hidden="true" />
                    <span className="min-w-0 flex-1 text-slate-700">{meta[b.key].label}</span>
                    <span className="tabular-nums font-medium text-slate-900">{b.count.toLocaleString()}</span>
                    <span className="w-10 text-right tabular-nums text-slate-600">{share(b.count, total)}</span>
                </li>
            ))}
        </ul>
    </div>
);

/** A labelled count with its own denominator ("29 of 30 accounts with app access"). */
/** `to`: when the count is non-zero, it links to the list showing exactly those rows. */
const CountRow = ({ label, count, of, hint, to }) => (
    <li className="flex items-start justify-between gap-4 py-2.5">
        <div className="min-w-0">
            <p className="text-sm text-slate-700">{label}</p>
            {hint && <p className="mt-0.5 text-xs text-slate-600">{hint}</p>}
        </div>
        <p className="shrink-0 text-right text-sm">
            {to && count > 0 ? (
                <Link
                    to={to}
                    aria-label={`View ${count.toLocaleString()}: ${label}`}
                    className={`rounded tabular-nums font-medium text-primary-700 underline-offset-2 hover:underline ${FOCUS_RING}`}
                >
                    {count.toLocaleString()}
                </Link>
            ) : (
                <span className="tabular-nums font-medium text-slate-900">{count.toLocaleString()}</span>
            )}
            {of && <span className="block text-xs text-slate-600">{of}</span>}
        </p>
    </li>
);

// Users list for one app with filters preset (only when the card is scoped to one app:
// a cross-app count has no single list that shows it).
const usersLink = (appId, filters) =>
    appId ? `/users?${new URLSearchParams({ app_id: appId, ...filters }).toString()}` : undefined;

const HealthCard = ({ icon: Icon, title, description, children }) => (
    <Card>
        <CardHeader>
            <CardTitle className="flex items-center gap-2">
                <Icon className="h-5 w-5 shrink-0" aria-hidden="true" />
                {title}
            </CardTitle>
            <CardDescription>{description}</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">{children}</CardContent>
    </Card>
);

const Note = ({ children }) => <p className="border-t border-slate-200 pt-3 text-sm text-slate-600">{children}</p>;

// Scoped to one app, the population is that app's active members.
const noun = (data) => (data.app_id ? 'active member' : 'active account');

const SignInCard = ({ data, scopeName }) => {
    const { accounts, sign_in: s } = data;
    const none = s.none_recorded;
    const noneTotal = none.created_since_records + none.created_before_records;
    const dormantFrom = s.records_since
        ? formatDate(new Date(new Date(s.records_since).getTime() + s.dormant.days * 86400000))
        : null;
    return (
        <HealthCard
            icon={LogIn}
            title="Last sign-in"
            description={`Most recent sign-in ${
                scopeName ? `to ${scopeName}` : 'to any app or this console'
            } for ${plural(accounts, noun(data))}. Sessions an admin started with “act as user” are not counted.`}
        >
            {s.records_since ? (
                <p className="text-sm text-slate-600">
                    Sign-in records start {formatDate(s.records_since)}; nothing earlier is stored.
                </p>
            ) : (
                <p className="text-sm text-slate-600">No sign-ins are on record yet.</p>
            )}
            <BucketBreakdown buckets={s.buckets} meta={SIGN_IN_BUCKETS} total={accounts} />
            {noneTotal > 0 && (
                <Note>
                    Of the {plural(noneTotal, 'account')} with no sign-in on record:{' '}
                    {none.created_since_records > 0 &&
                        `${none.created_since_records.toLocaleString()} created since records began and never signed in (${none.created_since_records_over_7d.toLocaleString()} of them created more than 7 days ago)`}
                    {none.created_since_records > 0 && none.created_before_records > 0 && '; '}
                    {none.created_before_records > 0 &&
                        `${none.created_before_records.toLocaleString()} created before records began, so they may have signed in earlier`}
                    .
                </Note>
            )}
            <Note>
                {s.dormant.count === null
                    ? `Dormant accounts (app access, no sign-in for ${
                          s.dormant.days
                      } days) can be counted once sign-in records span ${s.dormant.days} days${
                          dormantFrom ? `, from ${dormantFrom}` : ''
                      }.`
                    : `${plural(s.dormant.count, 'account')} with app access, created over ${
                          s.dormant.days
                      } days ago, ${s.dormant.count === 1 ? 'has' : 'have'} not signed in for ${s.dormant.days} days.`}
            </Note>
        </HealthCard>
    );
};

const PasswordCard = ({ data }) => {
    const { accounts, apps, password: p } = data;
    const recorded = accounts - (p.buckets.find((b) => b.key === 'none_recorded')?.count || 0);
    const appsOn =
        p.apps_recording === 0
            ? `none of ${plural(apps, 'app')} has it on`
            : `${p.apps_recording} of ${plural(apps, 'app')} have it on`;
    return (
        <HealthCard
            icon={KeyRound}
            title="Passwords"
            description="Time since each active account’s last recorded password change, and accounts stuck mid-setup."
        >
            {recorded === 0 ? (
                <p className="rounded-lg bg-slate-50 px-3 py-2.5 text-sm text-slate-700">
                    Password age isn’t available: no password changes are recorded. A change is recorded only in apps
                    with the Password history setting turned on, and {appsOn}.
                </p>
            ) : (
                <>
                    <BucketBreakdown buckets={p.buckets} meta={PASSWORD_BUCKETS} total={accounts} />
                    <Note>
                        Changes are recorded only in apps with the Password history setting on ({appsOn}). “No change
                        recorded” does not mean the password was never changed.
                    </Note>
                </>
            )}
            <ul className="divide-y divide-slate-100 border-t border-slate-200">
                <CountRow
                    label="Never set a password; temporary password expired"
                    hint="They can’t sign in until a new temporary password is issued."
                    count={p.temporary_first_expired}
                />
                <CountRow
                    label="Never set a password; temporary password still valid"
                    count={p.temporary_first_valid}
                />
                <CountRow label="Started a password reset and didn’t finish" count={p.reset_pending} />
                <CountRow label="No password and no temporary password" count={p.no_password} />
            </ul>
        </HealthCard>
    );
};

const SecondFactorCard = ({ data }) => {
    const { accounts, with_app_access: withAccess, apps, mfa } = data;
    const ofAccounts = `of ${plural(accounts, noun(data))}`;
    return (
        <HealthCard
            icon={ShieldCheck}
            title="Second factor"
            description={`Two-factor authentication (2FA) is on in ${mfa.apps_email_otp} of ${plural(
                apps,
                'app'
            )}; authenticator apps (TOTP) are allowed in ${mfa.apps_totp}.`}
        >
            <ul className="divide-y divide-slate-100">
                <CountRow
                    label="Can sign in to an app with a password alone"
                    hint="Member of an app with 2FA off and not enrolled in TOTP for it. Sign-ins to this console always ask for a code."
                    count={mfa.password_only_app}
                    of={`of ${plural(withAccess, 'account')} with app access`}
                />
                <CountRow
                    label="Enrolled in an authenticator app (TOTP)"
                    count={mfa.totp_enrolled}
                    of={ofAccounts}
                    to={mfa.apps_totp > 0 ? usersLink(data.app_id, { status: '1', mfa: '1' }) : undefined}
                />
                <CountRow label="Linked to an SSO identity" count={mfa.sso_linked} of={ofAccounts} />
                <CountRow label="Linked to Google or Apple sign-in" count={mfa.social_linked} of={ofAccounts} />
            </ul>
        </HealthCard>
    );
};

const LockoutCard = ({ data }) => {
    const { accounts, lockout } = data;
    const ofAccounts = `of ${plural(accounts, noun(data))}`;
    return (
        <HealthCard
            icon={LockKeyhole}
            title="Failed sign-ins"
            description="Wrong-password attempts are counted until the next successful sign-in; enough in a row locks the account."
        >
            <ul className="divide-y divide-slate-100">
                <CountRow
                    label="Locked now"
                    hint="Unlock from the Users page once the attempts are confirmed genuine."
                    count={lockout.locked_now}
                    of={ofAccounts}
                    to={usersLink(data.app_id, { status: '1', locked: '1' })}
                />
                <CountRow
                    label="Failed attempts since their last sign-in, not locked"
                    count={lockout.failed_not_locked}
                    of={ofAccounts}
                />
            </ul>
        </HealthCard>
    );
};

const AccountHealth = () => {
    const { apps } = useApps();
    const [appId, setAppId] = useState('');
    const [state, setState] = useState({ status: 'loading', data: null });

    const load = useCallback(async () => {
        setState((s) => ({ ...s, status: 'loading' }));
        try {
            const data = await dashboardService.getAccountHealth(appId);
            if (!data) throw new Error('Empty account health response');
            setState({ status: 'ready', data });
        } catch (err) {
            console.error('Error loading account health:', err);
            setState({ status: 'error', data: null });
        }
    }, [appId]);

    useEffect(() => {
        load();
    }, [load]);

    const appOptions = (apps || []).map((a) => ({ value: a.app_id, label: a.app_name }));
    const scopeName = appId ? appOptions.find((o) => o.value === appId)?.label || 'this app' : null;

    let body;
    if (state.status === 'loading') {
        body = (
            <div className="grid gap-6 lg:grid-cols-2" aria-busy="true" aria-label="Loading account health">
                {[1, 2, 3, 4].map((i) => (
                    <div key={i} className="h-72 animate-pulse rounded-xl bg-slate-200" />
                ))}
            </div>
        );
    } else if (state.status === 'error') {
        body = (
            <Card role="alert">
                <CardContent className="flex flex-col items-start gap-3 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-red-600" aria-hidden="true" />
                        <p className="text-sm text-slate-700">Couldn’t load account health. Try again.</p>
                    </div>
                    <Button variant="outline" onClick={load}>
                        <RotateCw className="h-4 w-4" />
                        Retry
                    </Button>
                </CardContent>
            </Card>
        );
    } else if (state.data.accounts === 0) {
        body = (
            <Card>
                <CardContent className="p-5 text-sm text-slate-600">
                    {scopeName ? `${scopeName} has no active members.` : 'There are no active accounts.'}
                </CardContent>
            </Card>
        );
    } else {
        body = (
            <div className="grid items-start gap-6 lg:grid-cols-2">
                <SignInCard data={state.data} scopeName={scopeName} />
                <PasswordCard data={state.data} />
                <SecondFactorCard data={state.data} />
                <LockoutCard data={state.data} />
            </div>
        );
    }

    return (
        <section aria-labelledby="account-health-heading">
            <div className="mb-3 flex flex-wrap items-center justify-between gap-3">
                <h2 id="account-health-heading" className="text-lg font-semibold text-slate-900">
                    Account health
                </h2>
                <label className="inline-flex items-center gap-2 text-sm text-slate-600">
                    <span className="sr-only sm:not-sr-only">Application</span>
                    <select
                        value={appId}
                        onChange={(e) => setAppId(e.target.value)}
                        className="h-9 rounded-md border border-slate-200 bg-white px-2 text-sm text-slate-900 shadow-sm outline-none focus-visible:border-primary-500 focus-visible:ring-2 focus-visible:ring-primary-200"
                    >
                        <option value="">All applications</option>
                        {appOptions.map((o) => (
                            <option key={o.value} value={o.value}>
                                {o.label}
                            </option>
                        ))}
                    </select>
                </label>
            </div>
            {body}
        </section>
    );
};

const Analytics = () => (
    <div className="space-y-6">
        <AccountHealth />
        <section aria-labelledby="activity-heading">
            <h2 id="activity-heading" className="mb-3 text-lg font-semibold text-slate-900">
                Activity over time
            </h2>
            <ActivityCharts />
        </section>
    </div>
);

export default Analytics;
